/**
 * The calendar days strip's span paint: one subcolumn layer's read
 * (`render`: the synchronous provider path, or the async read only the
 * latest of which may paint), the failed-layer rule, and the synchronous
 * paint (ghost projection, cell reconcile, layout) that a ghost or draft
 * change repeats from the layer's cached context.
 *
 * The strip reaches this module through `StripSpanPaint`; this module reaches
 * the strip only through `StripPaintHost`, whose members are readonly or
 * methods. The layers' contexts are written through `SpanContexts` only.
 *
 * A layer already painted for the same fragment under the bind's certified
 * read (`StripBindState.paintKey`) is not read or painted again: the same
 * read paints the same spans (perf 30, cached days).
 */

import { AsyncStateManager } from "@tento-core/async-state-manager";
import {
  type CalendarSubcolumn,
  type CalendarSubcolumnSpan,
  type SubcolumnCreateHandler,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { type Option } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import { CalendarColumnCell } from "@tento-ui/components/calendar/calendar-column-cell";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import { DateFragment, DateRegion } from "@tento-chrono";
import { GestureInfo } from "@tento-lona/sheets-ui/sheet/gestures/gesture-info.ts";
import { eventIdPromotionsOf } from "@tento-lona/platform";
import type { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { FrameMarks } from "@tento-ui/frame-marks";
import type { CalendarSheetColumn } from "./calendar-sheet-column.ts";
import { failedLayerKeepsPaint } from "./calendar-days-strip-read.ts";
import { recordReadFailure, recordReadJob, type StripBindState } from "./calendar-days-strip-bind.ts";
import { type DragGhostHost, type DragGhostState, hostsDragGhost, projectGhost } from "./calendar-days-strip-drag-ghost.ts";
import {
  bindSpanCell,
  markLayerReadFailed,
  poolSpanCell,
  POOLED_SPAN_CELL,
  reconcileSpanCells,
  spanCellPool,
  spanCells,
  spanCellsById,
  takeSpanCell,
  visibleSpanEntries,
} from "./calendar-days-strip-span-cells.ts";
import type { SpanContexts } from "./calendar-days-strip-span-contexts.ts";
import type { CalendarDaysStrip } from "./calendar-days-strip.ts";

/** What the span paint reaches on its strip. Readonly members and methods only. */
export interface StripPaintHost {
  /** The strip's bind state (read here; written by calendar-days-strip-bind.ts only). */
  readonly bind: StripBindState;
  /** The in-flight drag ghost (written by calendar-days-strip-drag-ghost.ts only). */
  readonly ghost: DragGhostState;
  /** What the drag ghost reaches on the strip (for which layer hosts the ghost). */
  readonly ghostHost: DragGhostHost;
  /** The strip's span layers and their last render contexts. */
  readonly contexts: SpanContexts;
  /** The dependencies `init` set that a paint reads (throws before `init`). */
  deps(): Pick<CalendarSheetColumn.Dependencies, "stripWork" | "gestureManager">;
  /** The signals `init` set that a paint reads (throws before `init`). */
  signals(): Pick<CalendarSheetColumn.Signals, "currentTime">;
  /** Tell the harness's paint observers (if any attached) that a paint begins; returns their settle, or null with none. */
  notifyPaint($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx): Option<() => void>;
}

/**
 * A layer paint's per-cell phases, each summed over the layer's
 * cells and recorded inside its `strip.paint` mark: `bind` (take or make the
 * cell, place and style it), `content` (the chip: title, colour, time
 * label) and `gesture` (pointer callbacks). `PAINT_PHASE` indexes
 * `PAINT_PHASE_CAUSES`; each paint owns its `FrameMarks.Phases` of them.
 */
export const PAINT_PHASE = { bind: 0, content: 1, gesture: 2 } as const;
export const PAINT_PHASE_CAUSES = ["strip.paint.bind", "strip.paint.content", "strip.paint.gesture"] as const;

/** A strip's span paint: reads and paints its subcolumn layers. */
export class StripSpanPaint {
  readonly #host: StripPaintHost;
  /** The latest read per layer: layers are reused across dates, so only the latest may paint. */
  readonly #reads = new AsyncStateManager<HTMLElement, Promise<CalendarSubcolumnSpan[]>>();
  /** This paint's per-cell phase marks (`PAINT_PHASE_CAUSES`). */
  readonly #phases = new FrameMarks.Phases(PAINT_PHASE_CAUSES);

  /** A paint for the strip `host` reaches. */
  constructor(host: StripPaintHost) {
    this.#host = host;
  }

  /** Read one subcolumn's spans for `windowed` (the day `column`) and paint them into its layer. */
  render(
    $layout: HTMLElement,
    rowId: RowLocalId,
    subcolumn: CalendarSubcolumn,
    occupancy: "shared" | "sole",
    dr: DateRegion,
    column: ColumnIndex.Key.Resolved.WithSubindex,
    windowed: DateFragment.Windowed,
    createHandler: Option<SubcolumnCreateHandler>,
  ): void {
    const host = this.#host;
    const effectiveSharing = occupancy === "shared" && subcolumn.stagger !== false;
    $layout.toggleAttribute("is-sharing", effectiveSharing);
    const width = subcolumn.width;
    switch (width.type) {
      case "preferred":
        // Distribute the day slot: the authored width is the preferred/max
        // (flex-basis), never a floor — lanes shrink to fit rather than
        // overflow. `min-width: 0` lets a lane shrink below its basis.
        $layout.style.flex = `0 1 ${width.width}`;
        $layout.style.minWidth = "0";
        $layout.style.width = "";
        break;
      case "flex":
        // Full share of the day slot; reset any flex basis a recycled $layout
        // carried.
        $layout.style.flex = "";
        $layout.style.minWidth = "";
        $layout.style.width = "";
        break;
      case "natural":
        // Content-sized, not shrinkable.
        $layout.style.flex = "";
        $layout.style.minWidth = "";
        $layout.style.width = "";
        $layout.style.flexShrink = "0";
        break;
      default: {
        const _exhaustive: never = width;
        return _exhaustive;
      }
    }

    const key = host.bind.paintKey;
    const shown = host.contexts.get($layout);
    if (key != null && host.ghost.current == null && host.contexts.paintedUnder($layout) === key &&
      sameFragment(shown?.windowed ?? null, windowed) && shown?.createHandler === createHandler) {
      // Any older async read of this layer is superseded by the paint it shows.
      this.#reads.clear($layout);
      return;
    }
    const spanContext: CalendarSubcolumn.SpanRenderContext = {
      windowed,
      tz: dr.tz,
      currentTime: host.signals().currentTime.get(),
      queryCache: host.bind.queryCache,
      bindRange: host.bind.range,
    };
    // Without the strip queue (the `strip-budget` flag off) the pre-F1 path
    // is kept whole: spans always arrive through `makeSpans`.
    const makeSpansSync = host.deps().stripWork == null ? undefined : subcolumn.makeSpansSync;
    if (makeSpansSync != null) {
      // A synchronous provider paints in this write task: no microtask hop,
      // no paint outside the frame budget. Any older async read of this layer
      // is superseded.
      this.#reads.clear($layout);
      let spans: CalendarSubcolumnSpan[];
      try {
        spans = makeSpansSync(spanContext);
      } catch (error) {
        this.#failLayer($layout, windowed);
        recordReadFailure(host.bind, String(error));
        return;
      }
      const ctx: CalendarDaysStrip.SpanRenderCtx = { spans, rowId, column, windowed, createHandler };
      host.contexts.set($layout, ctx, key);
      markLayerReadFailed($layout, false);
      this.paint($layout, ctx);
      return;
    }
    const spansPromise = subcolumn.makeSpans(spanContext);
    // The layout is reused across dates. Only its latest query may paint it;
    // date-keyed entries both retained settled results and let an older date
    // overwrite the layout after a scroll. Release each result after delivery.
    const bindGeneration = host.bind.generation;
    const stillValid = this.#reads.bind($layout, spansPromise);
    const painted = spansPromise.then((spans) => {
      if (!stillValid(spansPromise)) return;
      this.#reads.clear($layout);
      if (bindGeneration !== host.bind.generation) return;
      const ctx: CalendarDaysStrip.SpanRenderCtx = {
        spans,
        rowId,
        column,
        windowed,
        createHandler,
      };
      host.contexts.set($layout, ctx, key);
      markLayerReadFailed($layout, false);
      this.paint($layout, ctx);
    }, (error: Error) => {
      if (stillValid(spansPromise)) {
        this.#reads.clear($layout);
        if (bindGeneration === host.bind.generation) this.#failLayer($layout, windowed);
      }
      throw error;
    });
    recordReadJob(host.bind, painted);
  }

  /**
   * A current-generation read of `$layout` for `windowed` rejected. Layers
   * are reused across dates, so its last paint may be another date's cards:
   * unless it already shows this fragment, its cards are pooled and its
   * context dropped (a later draft change or drag must not repaint them).
   * Either way the strip reveals it marked failed.
   */
  #failLayer($layout: HTMLElement, windowed: DateFragment.Windowed): void {
    const shown = this.#host.contexts.get($layout);
    if (!failedLayerKeepsPaint(shown?.windowed ?? null, windowed)) {
      this.#host.contexts.delete($layout);
      for (const $cell of spanCells($layout)) poolSpanCell($cell);
    } else if (shown != null) {
      // Kept, but marked failed: the next read must paint (and clear the mark).
      this.#host.contexts.set($layout, shown, null);
    }
    markLayerReadFailed($layout, true);
  }

  /**
   * Synchronous span paint: applies the in-flight drag ghost then reconciles
   * cells by id (atomic reuse). Called from `render`'s resolve and from the
   * ghost and draft repaints (no re-`makeSpans`). The harness's `onPaint`
   * observers see every paint, the empty no-op included, before it runs.
   */
  paint($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx): void {
    const settle = this.#host.notifyPaint($layout, ctx);
    if (settle == null) return this.#runPaint($layout, ctx);
    try {
      this.#runPaint($layout, ctx);
    } finally {
      settle();
    }
  }

  #runPaint($layout: HTMLElement, ctx: CalendarDaysStrip.SpanRenderCtx): void {
    const host = this.#host;
    const ghost = host.ghost.current;
    // Keep the render context for later data/ghost updates, but an empty
    // layer has no cells to reconcile and needs no queued layout write.
    if (
      ctx.spans.length === 0 &&
      ghost == null &&
      $layout.childElementCount === 0
    ) return;

    const paintStart = FrameMarks.begin();
    let created = 0;
    const { rowId, column, windowed, createHandler } = ctx;
    const visible = visibleSpanEntries(
      projectGhost(
        ctx.spans,
        ghost,
        windowed,
        "drag",
        ghost != null && hostsDragGhost(host.ghostHost, $layout, ghost)
          ? "hosts-ghost"
          : "other-lane",
      ),
      windowed,
    );
    const existing = spanCellsById($layout);
    const promotions = host.bind.props != null
      ? eventIdPromotionsOf(host.bind.props.row)
      : null;
    const pool = spanCellPool($layout, visible, ghost == null, promotions);
    const isGroup = $layout instanceof CalendarColumnLayoutGroup;
    const rendered: CalendarColumnCell<HTMLElement>[] = [];
    const used = new Set<CalendarColumnCell<HTMLElement>>();

    for (const { span, time } of visible) {
      this.#phases.begin();
      const reused = takeSpanCell(span.id, existing, pool, promotions);
      if (reused == null) created++;
      const $cell = reused ??
        CalendarColumnCell.makeWith<HTMLElement>({ id: span.id, time });
      // A pooled cell's old lane belongs to its previous event. Keep it hidden,
      // like a new cell, until layout positions its new content. Live same-ID
      // cells and optimistic-ID promotions retain their current presentation.
      if (isGroup && !span.isDragging &&
        (reused == null || reused.hasAttribute(POOLED_SPAN_CELL))) {
        CalendarColumnLayoutGroup.markPendingLayout($cell);
      } else if (!isGroup || span.isDragging) {
        // These surfaces have no layout commit that could reveal the cell.
        CalendarColumnLayoutGroup.clearPendingLayout($cell);
      }
      used.add($cell);
      rendered.push($cell);

      const $span = bindSpanCell($cell, span, time, this.#phases);
      this.#phases.split(PAINT_PHASE.bind);
      if (span.callbacks) {
        $cell.bindCallbacks(span.callbacks, "span");
      } else {
        const gestureInfo = new GestureInfo(
          rowId,
          span.item ?? null,
          column,
          windowed,
          createHandler,
        );
        $cell.bindCallbacks(
          span.item != null
            ? host.deps().gestureManager.asCellDelegate(gestureInfo)
            : host.deps().gestureManager.asEmptyCellDelegate(gestureInfo),
          "span",
        );
      }
      this.#phases.split(PAINT_PHASE.gesture);

      $span.assignStyles({
        "--width": $layout.style.width,
        "--start": String(span.start.mse),
        "--end": span.end ? String(span.end.mse) : String(span.start.mse),
      });
      // Decorative drag visuals: the floating ghost stays fully opaque with an
      // elevated shadow; the original source span becomes a faint placeholder
      // via `drag-source`. Pointer events stay on so the gesture keeps tracking.
      $cell.style.opacity = "";
      $cell.toggleAttribute("drag-ghost", span.isDragging ?? false);
      $cell.toggleAttribute("drag-source", span.isDragSource ?? false);
      this.#phases.split(PAINT_PHASE.bind);
    }
    this.#phases.flush(0);

    reconcileSpanCells($layout, rendered, used);

    if ($layout instanceof CalendarColumnLayoutGroup) {
      // The strip holds the layer's cells: lay them out directly instead of
      // re-reading the layer's children (F14). Pooled cells are hidden and
      // carry no layout info, exactly as `visibleCells()` would filter them.
      $layout.layout(rendered.flatMap(($cell) => {
        const info = $cell.visibleLayoutInfo;
        return info == null ? [] : [{ $e: $cell, info }];
      }));
    }
    FrameMarks.end("strip.paint", paintStart, created, rendered.length - created);
  }
}

/** Whether `shown` (a layer's last context's fragment) is `windowed`'s fragment: same zone, start and end. */
function sameFragment(shown: Option<DateFragment.Windowed>, windowed: DateFragment.Windowed): boolean {
  if (shown == null) return false;
  if (shown === windowed) return true;
  const a = shown.fragment, b = windowed.fragment;
  return a.parent.fullname === b.parent.fullname && a.start.mse === b.start.mse && a.end.mse === b.end.mse;
}
